import { trackDownload } from '../utils/downloadIndicator';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PushPinIcon from '@mui/icons-material/PushPin';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import DrawIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ShareIcon from '@mui/icons-material/Share';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import ActivityDetails from './ActivityDetails';
import { blobsApi, filesApi, planShareApi } from '../services/api';

const ROTATION_STEP = 90;

const normalizeRotation = (rotation) => ((rotation % 360) + 360) % 360;

const mapPointToOriginalRotation = (x, y, rotation) => {
  switch (normalizeRotation(rotation)) {
    case 90:
      return { x: y, y: 1 - x };
    case 180:
      return { x: 1 - x, y: 1 - y };
    case 270:
      return { x: 1 - y, y: x };
    default:
      return { x, y };
  }
};

/**
 * Reusable Image Viewer Component
 * Displays images in a modern full-screen modal with navigation, zoom, and pan controls
 * Supports pin placement for plan images and drawing/annotation
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog closes
 * @param {string} props.imageUrl - Current image URL
 * @param {string} props.fileName - Current file name for display
 * @param {string} props.uploadedDate - Optional uploaded date for display
 * @param {Array} props.allImages - Array of all image objects with {url, file_name, file_id, uploaded_date} properties
 * @param {number} props.currentIndex - Index of current image
 * @param {function} props.onNavigate - Callback when navigating between images (direction: 'next'|'prev')
 * @param {function} props.onDelete - Optional callback for delete action
 * @param {boolean} props.isLoading - Whether images are loading
 * @param {Array} props.pins - Array of pin objects with {activity_id, title, PinX, PinY, is_completed} properties
 * @param {boolean} props.enablePinMode - Whether pin placement mode is enabled
 * @param {function} props.onPinClick - Callback when clicking an existing pin (activity)
 * @param {function} props.onAddPin - Callback when placing a new pin (x, y) in percentage coordinates
 * @param {boolean} props.isPlanImage - Whether this is a plan image (enables pin functionality)
 * @param {Object} props.selectedPin - The currently selected pin (activity) to show details for
 * @param {function} props.onClosePinDetails - Callback to close the pin details pane
 * @param {Array} props.workers - List of workers for assignment/mentions
 * @param {function} props.onDrawingSaved - Callback when drawing is saved (file_id passed)
 * @param {function} props.onOpenActivity - Optional callback for opening an activity linked to the current image
 * @param {function} props.onOpenForm - Optional callback for opening a form linked to the current image
 */
const ImageViewer = ({
  open,
  onClose,
  imageUrl,
  fileName,
  uploadedDate,
  allImages = [],
  currentIndex = 0,
  onNavigate,
  onDelete,
  isLoading = false,
  pins = [],
  enablePinMode = false,
  onPinClick,
  onAddPin,
  onTogglePinMode,
  isPlanImage = false,
  selectedPin = null,
  onClosePinDetails,
  workers = [],
  onEditActivity,
  onDeleteActivity,
  onDrawingSaved,
  refreshKey = 0,
  readOnly = false,
  canShare = true,
  resolvePublicPhotoUrl = null,
  onOpenActivity,
  onOpenForm,
  onOpenPlan,
}) => {
  const canNavigate = allImages.length > 1;
  const showCounter = canNavigate;
  const currentImage = allImages?.[currentIndex] || null;
  const currentActivityLinks = Array.isArray(currentImage?.linked_activities) && currentImage.linked_activities.length > 0
    ? currentImage.linked_activities
    : (currentImage?.activity_id
      ? [{ activity_id: currentImage.activity_id, title: currentImage.activity_title }]
      : []);
  const currentFormLinks = Array.isArray(currentImage?.linked_forms)
    ? currentImage.linked_forms
    : [];

  const linkedRecordButtonSx = {
    minWidth: 0,
    p: 0,
    color: '#90caf9',
    fontSize: '12px',
    lineHeight: 1.3,
    textTransform: 'none',
    '&:hover': {
      color: '#bbdefb',
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  };

  const renderLinkedRecordLinks = () => (
    <>
      {currentActivityLinks.map((activity) => (
        <Button
          key={`activity-${activity.activity_id}`}
          size="small"
          variant="text"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.85rem !important' }} />}
          onClick={() => onOpenActivity?.(activity.activity_id)}
          disabled={!onOpenActivity}
          title={`Open activity: ${activity.title || `Activity #${activity.activity_id}`}`}
          sx={linkedRecordButtonSx}
        >
          Activity: {activity.title || `#${activity.activity_id}`}
        </Button>
      ))}
      {currentFormLinks.map((form) => (
        <Button
          key={`form-${form.project_form_id}`}
          size="small"
          variant="text"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.85rem !important' }} />}
          onClick={() => onOpenForm?.(form.project_form_id)}
          disabled={!onOpenForm}
          title={`Open form: ${form.title || form.template_name || `Form #${form.project_form_id}`}`}
          sx={linkedRecordButtonSx}
        >
          Form: {form.title || form.template_name || `#${form.project_form_id}`}
        </Button>
      ))}
    </>
  );

  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isWheelZooming, setIsWheelZooming] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [rotation, setRotation] = useState(0);
  
  // Cache buster state to force image reload after saving
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  
  // Drawing state
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingColor, setDrawingColor] = useState('#FF0000');
  const [brushSize, setBrushSize] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSavingDrawing, setIsSavingDrawing] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveMessageType, setSaveMessageType] = useState('success');
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const [showZoomHint, setShowZoomHint] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [savedPlanUrl, setSavedPlanUrl] = useState(null);
  const savedPlanFileRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyEtag, setHistoryEtag] = useState(null);
  const [historyPath, setHistoryPath] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRestoring, setHistoryRestoring] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const historyPageLoadingRef = useRef(false);

  useEffect(() => {
    if (open) setImageLoaded(false);
    setSavedPlanUrl(null);
    savedPlanFileRef.current = null;
    setHistoryOpen(false);
    planStrokesRef.current = [];
    activePlanStrokeRef.current = null;
  }, [open, imageUrl, currentIndex]);

  // Share Dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogLink, setShareDialogLink] = useState('');

  useEffect(() => {
    if (readOnly && isDrawingMode) {
      setIsDrawingMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  const getCurrentFileId = () => {
    const current = allImages?.[currentIndex];
    return current?.file_id ?? current?.fileId ?? null;
  };

  const openVersionHistory = async () => {
    const path = (savedPlanFileRef.current || allImages[currentIndex])?.blob_path;
    if (!path) return;
    setHistoryPath(path);
    setHistoryOpen(true);
    setSelectedVersion(null);
    setHistoryVersions([]);
    setHistoryError('');
    setHistoryLoading(true);
    try {
      const response = await blobsApi.versions(path);
      setHistoryVersions(response.data.versions || []);
      setHistoryCursor(response.data.next_cursor || null);
      setHistoryEtag(response.data.current_etag);
    } catch (error) {
      setHistoryError(error.response?.data?.error || 'Could not load version history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadOlderVersions = async () => {
    if (!historyCursor || !historyPath || historyPageLoadingRef.current) return;
    historyPageLoadingRef.current = true;
    setHistoryLoading(true);
    try {
      const response = await blobsApi.versions(historyPath, historyCursor);
      setHistoryVersions((current) => [...current, ...(response.data.versions || [])]);
      setHistoryCursor(response.data.next_cursor || null);
    } catch (error) {
      setHistoryError(error.response?.data?.error || 'Could not load older versions');
    } finally {
      historyPageLoadingRef.current = false;
      setHistoryLoading(false);
    }
  };

  const restoreSelectedVersion = async () => {
    if (!historyPath || !selectedVersion || !historyEtag) return;
    setHistoryRestoring(true);
    setHistoryError('');
    try {
      const response = await blobsApi.restoreVersion(historyPath, selectedVersion.version_id, historyEtag);
      setSavedPlanUrl(response.data.url || imageUrl);
      setCacheBuster(Date.now());
      setHistoryOpen(false);
      setIsDrawingMode(false);
      handleClearDrawing();
      showMessage('Image version restored');
      if (onDrawingSaved) {
        try {
          await onDrawingSaved(savedPlanFileRef.current || historyPath);
        } catch (refreshError) {
          console.warn('Image restored but parent refresh failed:', refreshError);
        }
      }
    } catch (error) {
      setHistoryError(error.response?.data?.error || 'Could not restore image version');
    } finally {
      setHistoryRestoring(false);
    }
  };

  const showMessage = (message, severity = 'success') => {
    setSaveMessage(message);
    setSaveMessageType(severity);
    setShowSaveMessage(true);
  };

  const handleShareLink = async () => {
    const fileId = getCurrentFileId();
    if (!fileId) return;

    try {
      const res = await planShareApi.createLink(fileId);
      const token = res.data?.token;
      if (!token) {
        showMessage('Failed to create share link', 'error');
        return;
      }
      const url = `${window.location.origin}/share/plan/${token}`;
      
      // Try navigator.share on supported mobile browsers
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Shared Plan',
            url: url
          });
          return;
        } catch (e) {
          // Fall back to copy if user cancels or it fails
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        showMessage('Share link copied to clipboard');
      } catch {
        // Safe fallback - open the nice copy pop up interface
        setShareDialogLink(url);
        setShareDialogOpen(true);
      }
    } catch (e) {
      console.error('Error creating share link:', e);
      showMessage('Failed to create share link', 'error');
    }
  };

  const handleCopyFromDialog = () => {
    try {
      // First try the modern API directly
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareDialogLink);
      } else {
        // Legacy fallback that works better when there is a UI interaction (button click in dialog)
        const textarea = document.createElement('textarea');
        textarea.value = shareDialogLink;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      showMessage('Share link copied to clipboard');
      setShareDialogOpen(false);
    } catch (err) {
      console.error('Failed to copy', err);
      showMessage('Failed to copy. Please manually select the link text and copy.', 'error');
    }
  };

  const handleExportPlanPdf = trackDownload(async () => {
    const fileId = getCurrentFileId();
    if (!fileId) return;
    try {
      const res = await planShareApi.exportPlanPdf(fileId);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plan-${fileId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showMessage('Plan PDF downloaded');
    } catch (e) {
      console.error('Error exporting plan PDF:', e);
      showMessage('Failed to export plan PDF', 'error');
    }
  });
  
  // Image layout state for aligning canvas and pins
  const [imageLayout, setImageLayout] = useState({ width: 0, height: 0, top: 0, left: 0 });
  
  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);
  const drawingCanvasRef = useRef(null);
  const drawingContextRef = useRef(null);
  const planStrokesRef = useRef([]);
  const activePlanStrokeRef = useRef(null);
  const zoomRef = useRef(zoom);
  const panXRef = useRef(panX);
  const panYRef = useRef(panY);
  const isWheelZoomingRef = useRef(false);
  const wheelZoomEndTimerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const updateImageLayout = () => {
    if (imageRef.current) {
      const { offsetLeft, offsetTop, width, height } = imageRef.current;
      setImageLayout({
        left: offsetLeft,
        top: offsetTop,
        width: width,
        height: height
      });
    }
  };

  useEffect(() => {
    // Initial update
    updateImageLayout();

    // Handle window resize
    window.addEventListener('resize', updateImageLayout);
    
    // Handle container resize (e.g. sidebar opening)
    let observer;
    if (imageContainerRef.current) {
      observer = new ResizeObserver(() => {
        // Small delay to ensure layout has settled
        requestAnimationFrame(updateImageLayout);
      });
      observer.observe(imageContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateImageLayout);
      if (observer) observer.disconnect();
    };
  }, [selectedPin, imageUrl, open]);
  
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.1;

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    panXRef.current = panX;
  }, [panX]);

  useEffect(() => {
    panYRef.current = panY;
  }, [panY]);

  useEffect(() => () => {
    if (wheelZoomEndTimerRef.current) {
      clearTimeout(wheelZoomEndTimerRef.current);
    }
  }, []);

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const setViewTransform = useCallback((nextZoom, nextPanX, nextPanY) => {
    zoomRef.current = nextZoom;
    panXRef.current = nextPanX;
    panYRef.current = nextPanY;
    setZoom(nextZoom);
    setPanX(nextPanX);
    setPanY(nextPanY);
  }, []);

  const getPanBounds = useCallback((nextZoom) => {
    const container = imageContainerRef.current;
    const image = imageRef.current;
    if (!container || !image) {
      return { maxX: 0, maxY: 0 };
    }

    const isQuarterTurn = rotation % 180 !== 0;
    const displayedImageWidth = image.width || image.offsetWidth || image.clientWidth;
    const displayedImageHeight = image.height || image.offsetHeight || image.clientHeight;
    const baseImageWidth = isQuarterTurn ? displayedImageHeight : displayedImageWidth;
    const baseImageHeight = isQuarterTurn ? displayedImageWidth : displayedImageHeight;
    const scaledImageWidth = baseImageWidth * nextZoom;
    const scaledImageHeight = baseImageHeight * nextZoom;

    return {
      maxX: Math.max(0, (scaledImageWidth - container.clientWidth) / 2),
      maxY: Math.max(0, (scaledImageHeight - container.clientHeight) / 2),
    };
  }, [rotation]);

  const canPanAtZoom = useCallback((nextZoom) => {
    if (nextZoom <= MIN_ZOOM) return false;
    const bounds = getPanBounds(nextZoom);
    return bounds.maxX > 0 || bounds.maxY > 0;
  }, [getPanBounds]);

  const getClampedPan = useCallback((nextPanX, nextPanY, nextZoom) => {
    if (nextZoom <= MIN_ZOOM) {
      return { x: 0, y: 0 };
    }

    const bounds = getPanBounds(nextZoom);

    return {
      x: clamp(nextPanX, -bounds.maxX, bounds.maxX),
      y: clamp(nextPanY, -bounds.maxY, bounds.maxY),
    };
  }, [getPanBounds]);

  const markWheelZooming = useCallback(() => {
    if (!isWheelZoomingRef.current) {
      isWheelZoomingRef.current = true;
      setIsWheelZooming(true);
    }

    if (wheelZoomEndTimerRef.current) {
      clearTimeout(wheelZoomEndTimerRef.current);
    }

    wheelZoomEndTimerRef.current = setTimeout(() => {
      isWheelZoomingRef.current = false;
      setIsWheelZooming(false);
    }, 120);
  }, []);

  // Reset zoom and pan when image changes
  useEffect(() => {
    setViewTransform(1, 0, 0);
    setIsWheelZooming(false);
    isWheelZoomingRef.current = false;
    isDraggingRef.current = false;
    setIsDragging(false);
    if (wheelZoomEndTimerRef.current) {
      clearTimeout(wheelZoomEndTimerRef.current);
      wheelZoomEndTimerRef.current = null;
    }
    setRotation(0);
    setIsDrawingMode(false);
  }, [imageUrl, setViewTransform]);

  // Show zoom hint when user zooms in
  useEffect(() => {
    if (zoom > 1) {
      setShowZoomHint(true);
      const timer = setTimeout(() => {
        setShowZoomHint(false);
      }, 4000); // Hide after 4 seconds
      return () => clearTimeout(timer);
    }
  }, [zoom]);

  // Keep the canvas and its strokes when zoom changes.
  useEffect(() => {
    if (!isDrawingMode || !imageRef.current || !drawingCanvasRef.current) return;

    const image = imageRef.current;
    const canvas = drawingCanvasRef.current;

    // Set canvas size to match the NATURAL image dimensions (unscaled)
    // This ensures coordinates are consistent regardless of zoom
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    const context = canvas.getContext('2d');
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = brushSize;
    context.strokeStyle = drawingColor;
    drawingContextRef.current = context;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawingMode, imageUrl]);

  // Update brush color
  useEffect(() => {
    if (drawingContextRef.current) {
      drawingContextRef.current.strokeStyle = drawingColor;
    }
  }, [drawingColor]);

  // Update brush size
  useEffect(() => {
    if (drawingContextRef.current) {
      drawingContextRef.current.lineWidth = brushSize;
    }
  }, [brushSize]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  // Helper to add cache buster to URL
  const getDisplayUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    try {
      const urlObj = new URL(url, window.location.origin);
      urlObj.searchParams.set('cb', cacheBuster);
      return urlObj.toString();
    } catch (e) {
      return url;
    }
  };

  // Drawing functions
  const pointOnDrawingCanvas = (clientX, clientY) => {
    const canvasRect = drawingCanvasRef.current.getBoundingClientRect();
    const image = imageRef.current;
    const imageRect = image.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(image.naturalWidth - 1,
        (clientX - canvasRect.left) * image.naturalWidth / imageRect.width)),
      y: Math.max(0, Math.min(image.naturalHeight - 1,
        (clientY - canvasRect.top) * image.naturalHeight / imageRect.height)),
    };
  };

  const startPlanStroke = (point) => {
    if (!isPlanImage) return;
    const image = imageRef.current;
    const stroke = {
      color: drawingColor,
      width: Math.min(0.05, brushSize / image.naturalWidth),
      points: [{ x: point.x / Math.max(1, image.naturalWidth - 1), y: point.y / Math.max(1, image.naturalHeight - 1) }],
    };
    planStrokesRef.current.push(stroke);
    activePlanStrokeRef.current = stroke;
  };

  const addPlanStrokePoint = (point) => {
    if (!isPlanImage || !activePlanStrokeRef.current) return;
    const image = imageRef.current;
    activePlanStrokeRef.current.points.push({
      x: point.x / Math.max(1, image.naturalWidth - 1),
      y: point.y / Math.max(1, image.naturalHeight - 1),
    });
  };

  const handleDrawingMouseDown = (e) => {
    if (!drawingCanvasRef.current || !drawingContextRef.current || !imageRef.current) return;

    e.preventDefault();

    const { x, y } = pointOnDrawingCanvas(e.clientX, e.clientY);

    setIsDrawing(true);
    startPlanStroke({ x, y });
    drawingContextRef.current.beginPath();
    drawingContextRef.current.moveTo(x, y);
  };

  const handleDrawingMouseMove = (e) => {
    if (!isDrawing || !drawingCanvasRef.current || !drawingContextRef.current || !imageRef.current) return;

    const { x, y } = pointOnDrawingCanvas(e.clientX, e.clientY);

    addPlanStrokePoint({ x, y });
    drawingContextRef.current.lineTo(x, y);
    drawingContextRef.current.stroke();
  };

  const handleDrawingMouseUp = () => {
    if (drawingContextRef.current) {
      drawingContextRef.current.closePath();
    }
    setIsDrawing(false);
    activePlanStrokeRef.current = null;
  };

  const handleDrawingMouseLeave = () => {
    if (drawingContextRef.current) {
      drawingContextRef.current.closePath();
    }
    setIsDrawing(false);
    activePlanStrokeRef.current = null;
  };

  const handleDrawingTouchStart = (e) => {
    if (!drawingCanvasRef.current || !drawingContextRef.current || !imageRef.current) return;

    e.preventDefault(); // Prevent scrolling/gestures

    const { x, y } = pointOnDrawingCanvas(e.touches[0].clientX, e.touches[0].clientY);

    setIsDrawing(true);
    startPlanStroke({ x, y });
    drawingContextRef.current.beginPath();
    drawingContextRef.current.moveTo(x, y);
  };

  const handleDrawingTouchMove = (e) => {
    if (!isDrawing || !drawingCanvasRef.current || !drawingContextRef.current || !imageRef.current) return;

    e.preventDefault(); // Prevent scrolling while drawing

    const { x, y } = pointOnDrawingCanvas(e.touches[0].clientX, e.touches[0].clientY);

    addPlanStrokePoint({ x, y });
    drawingContextRef.current.lineTo(x, y);
    drawingContextRef.current.stroke();
  };

  const handleDrawingTouchEnd = () => {
    if (drawingContextRef.current) {
      drawingContextRef.current.closePath();
    }
    setIsDrawing(false);
    activePlanStrokeRef.current = null;
  };

  const handleClearDrawing = () => {
    planStrokesRef.current = [];
    activePlanStrokeRef.current = null;
    if (drawingCanvasRef.current && drawingContextRef.current) {
      drawingContextRef.current.clearRect(0, 0, drawingCanvasRef.current.width, drawingCanvasRef.current.height);
    }
  };

  const mergeCanvasWithImage = async () => {
    if (!imageRef.current || !drawingCanvasRef.current) {
      throw new Error('Image or drawing canvas not found');
    }

    try {
      const drawingCanvas = drawingCanvasRef.current;
      
      // Get the blob_path for the current image
      const currentImage = savedPlanFileRef.current || allImages[currentIndex];
      if (!currentImage || !currentImage.blob_path) {
          throw new Error('Current image blob_path not found');
      }

      // Fetch the image blob through the backend proxy to avoid CORS issues
      const response = await blobsApi.download(currentImage.blob_path);
      const blob = response.data;
      
      const imgBitmap = await createImageBitmap(blob);

      // Create a new canvas to merge on
      const mergeCanvas = document.createElement('canvas');
      mergeCanvas.width = imgBitmap.width;
      mergeCanvas.height = imgBitmap.height;
      const mergeContext = mergeCanvas.getContext('2d');

      // Draw the fetched image (which is now CORS-safe)
      mergeContext.drawImage(imgBitmap, 0, 0);

      // Then overlay the drawing canvas
      mergeContext.drawImage(drawingCanvas, 0, 0, drawingCanvas.width, drawingCanvas.height);

      // Keep plans lossless at their native resolution. Photos retain their
      // existing JPEG output to avoid substantially larger photo files.
      return new Promise((resolve, reject) => {
        mergeCanvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Canvas toBlob failed'));
            }
        }, isPlanImage ? 'image/png' : 'image/jpeg', isPlanImage ? undefined : 0.92);
      });

    } catch (error) {
      console.error('Error merging canvas:', error);
      throw error;
    }
  };

  const handleSaveDrawing = async () => {
    if (!allImages[currentIndex]) return;

    setIsSavingDrawing(true);
    try {
      const blobPath = allImages[currentIndex].blob_path;
      if (!blobPath) {
        throw new Error('No blob_path available for this image');
      }
      
      let savedFile = null;
      const fileId = getCurrentFileId();
      if (isPlanImage) {
        if (!fileId) throw new Error('Plan file ID is missing');
        if (!planStrokesRef.current.length) throw new Error('Draw on the plan before saving');
        const response = await filesApi.savePlanStrokes(fileId, planStrokesRef.current);
        savedFile = response.data?.file || null;
        savedPlanFileRef.current = savedFile;
        try {
          const urlResponse = await filesApi.getUrl(fileId);
          setSavedPlanUrl(urlResponse.data.url);
        } catch (urlError) {
          console.warn('Plan saved, but its new URL could not be loaded:', urlError);
        }
      } else {
        const blob = await mergeCanvasWithImage();
        await blobsApi.saveDrawing(blobPath, blob, fileName);
      }

      // Force image refresh
      setCacheBuster(Date.now());

      setSaveMessage('Drawing saved successfully!');
      setSaveMessageType('success');
      setShowSaveMessage(true);
      setIsDrawingMode(false);
      handleClearDrawing();

      // Notify parent component if callback provided
      if (onDrawingSaved) await onDrawingSaved(savedFile || blobPath);

      // Close message after 3 seconds
      setTimeout(() => {
        setShowSaveMessage(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving drawing:', error);
      setSaveMessage(error.response?.data?.error || error.message || 'Error saving drawing. Please try again.');
      setSaveMessageType('error');
      setShowSaveMessage(true);
    } finally {
      setIsSavingDrawing(false);
    }
  };


  const handlePrevious = useCallback(() => {
    if (onNavigate && canNavigate) {
      onNavigate('prev');
    }
  }, [onNavigate, canNavigate]);

  const handleNext = useCallback(() => {
    if (onNavigate && canNavigate) {
      onNavigate('next');
    }
  }, [onNavigate, canNavigate]);

  const handleZoomIn = useCallback(() => {
    const nextZoom = Math.min(zoomRef.current + ZOOM_STEP, MAX_ZOOM);
    const clampedPan = getClampedPan(panXRef.current, panYRef.current, nextZoom);
    setViewTransform(nextZoom, clampedPan.x, clampedPan.y);
  }, [getClampedPan, setViewTransform]);

  const handleZoomOut = useCallback(() => {
    const nextZoom = Math.max(zoomRef.current - ZOOM_STEP, MIN_ZOOM);
    const clampedPan = getClampedPan(panXRef.current, panYRef.current, nextZoom);
    setViewTransform(nextZoom, clampedPan.x, clampedPan.y);
  }, [getClampedPan, setViewTransform]);

  const handleResetZoom = useCallback(() => {
    setViewTransform(1, 0, 0);
  }, [setViewTransform]);

  const handleRotateLeft = useCallback(() => {
    setRotation((prev) => normalizeRotation(prev - ROTATION_STEP));
  }, []);

  const handleRotateRight = useCallback(() => {
    setRotation((prev) => normalizeRotation(prev + ROTATION_STEP));
  }, []);

  const handleToggleDrawingMode = () => {
    if (!isDrawingMode) {
      setRotation(0);
    } else {
      handleClearDrawing();
    }
    setIsDrawingMode((prev) => !prev);
  };

  const handleWheel = useCallback((e) => {
    // Disable zooming in drawing mode
    if (isDrawingMode) {
      return;
    }

    e.preventDefault();
    
    const container = imageContainerRef.current;
    if (!container || !imageRef.current) return;

    // Get container and image dimensions
    const containerRect = container.getBoundingClientRect();
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;

    // Mouse position relative to container center
    const mouseX = e.clientX - containerRect.left - containerCenterX;
    const mouseY = e.clientY - containerRect.top - containerCenterY;

    const oldZoom = zoomRef.current;
    const normalizedDeltaY = e.deltaMode === 1
      ? e.deltaY * 16
      : e.deltaMode === 2
        ? e.deltaY * containerRect.height
        : e.deltaY;
    const newZoom = clamp(oldZoom * Math.exp(-normalizedDeltaY * 0.002), MIN_ZOOM, MAX_ZOOM);

    // Adjust pan to keep mouse position fixed
    if (newZoom !== oldZoom) {
      const zoomChange = newZoom / oldZoom;
      const nextPanX = panXRef.current * zoomChange - mouseX * (zoomChange - 1);
      const nextPanY = panYRef.current * zoomChange - mouseY * (zoomChange - 1);
      const clampedPan = getClampedPan(nextPanX, nextPanY, newZoom);
      markWheelZooming();
      setViewTransform(newZoom, clampedPan.x, clampedPan.y);
    }
  }, [getClampedPan, isDrawingMode, markWheelZooming, setViewTransform]);

  const handleMouseDown = useCallback((e) => {
    // If in drawing mode, don't start dragging
    if (isDrawingMode) {
      return;
    }
    
    // If in pin mode, don't start dragging
    if (enablePinMode && isPlanImage) {
      return;
    }
    
    if (zoomRef.current > 1 && canPanAtZoom(zoomRef.current) && e.button === 0) { // Only on left mouse button
      e.preventDefault();
      const nextDragStart = { x: e.clientX - panXRef.current, y: e.clientY - panYRef.current };
      isDraggingRef.current = true;
      dragStartRef.current = nextDragStart;
      setIsDragging(true);
    }
  }, [canPanAtZoom, enablePinMode, isDrawingMode, isPlanImage]);

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current || zoomRef.current <= 1) return;

    e.preventDefault();
    const newPanX = e.clientX - dragStartRef.current.x;
    const newPanY = e.clientY - dragStartRef.current.y;
    const clampedPan = getClampedPan(newPanX, newPanY, zoomRef.current);
    setViewTransform(zoomRef.current, clampedPan.x, clampedPan.y);
  }, [getClampedPan, setViewTransform]);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);
  
  const handleImageClick = (e) => {
    // Only handle clicks when in pin mode on plan images
    if (!enablePinMode || !isPlanImage || !onAddPin || !imageRef.current || !imageContainerRef.current) {
      return;
    }
    
    e.stopPropagation();
    
    // Get click position relative to the image
    const image = imageRef.current;
    const imageRect = image.getBoundingClientRect();
    
    // Calculate click position relative to the displayed image
    const clickX = e.clientX - imageRect.left;
    const clickY = e.clientY - imageRect.top;
    
    // Convert to percentage coordinates (0-100)
    const rotatedX = clickX / imageRect.width;
    const rotatedY = clickY / imageRect.height;
    const originalPoint = mapPointToOriginalRotation(rotatedX, rotatedY, rotation);
    const pinX = originalPoint.x * 100;
    const pinY = originalPoint.y * 100;
    
    // Validate coordinates are within image bounds
    if (pinX >= 0 && pinX <= 100 && pinY >= 0 && pinY <= 100) {
      onAddPin(pinX, pinY);
    }
  };

  const handleTouchStart = useCallback((e) => {
    if (isDrawingMode) return;

    if (e.touches.length === 1) {
      // Single touch - start panning if zoomed in
      if (zoomRef.current > 1 && canPanAtZoom(zoomRef.current)) {
        const nextDragStart = {
          x: e.touches[0].clientX - panXRef.current,
          y: e.touches[0].clientY - panYRef.current
        };
        isDraggingRef.current = true;
        dragStartRef.current = nextDragStart;
        setIsDragging(true);
      }
    } else if (e.touches.length === 2) {
      // Double touch - start pinching
      isDraggingRef.current = false;
      setIsDragging(false);
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const nextDragStart = { distance };
      dragStartRef.current = nextDragStart;
    }
  }, [canPanAtZoom, isDrawingMode]);

  const handleTouchMove = useCallback((e) => {
    if (isDrawingMode) return;

    if (e.touches.length === 1 && isDraggingRef.current && zoomRef.current > 1) {
      // Single touch - panning
      e.preventDefault(); // Prevent scrolling
      const newPanX = e.touches[0].clientX - dragStartRef.current.x;
      const newPanY = e.touches[0].clientY - dragStartRef.current.y;
      const clampedPan = getClampedPan(newPanX, newPanY, zoomRef.current);
      setViewTransform(zoomRef.current, clampedPan.x, clampedPan.y);
    } else if (e.touches.length === 2 && dragStartRef.current.distance !== undefined) {
      // Double touch - pinching
      e.preventDefault();
      const currentDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );

      const delta = currentDistance - dragStartRef.current.distance;
      
      if (Math.abs(delta) > 5) {
        const scaleFactor = currentDistance / dragStartRef.current.distance;
        const newZoom = Math.min(Math.max(zoomRef.current * scaleFactor, MIN_ZOOM), MAX_ZOOM);
        const clampedPan = getClampedPan(panXRef.current, panYRef.current, newZoom);
        setViewTransform(newZoom, clampedPan.x, clampedPan.y);
        const nextDragStart = { distance: currentDistance };
        dragStartRef.current = nextDragStart;
      }
    }
  }, [getClampedPan, isDrawingMode, setViewTransform]);

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
  }, []);

  const canPan = canPanAtZoom(zoom);

  const handleKeyDown = useCallback((e) => {
    // Disable keyboard zoom shortcuts in drawing mode
    if (isDrawingMode) {
      return;
    }

    const target = e.target;
    const isEditableTarget =
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable);

    if (isEditableTarget) {
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePrevious();
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNext();
      return;
    }

    if (e.key === '+' || e.key === '=') {
      handleZoomIn();
    } else if (e.key === '-') {
      handleZoomOut();
    } else if (e.key === '0') {
      handleResetZoom();
    } else if (e.key === '[') {
      handleRotateLeft();
    } else if (e.key === ']') {
      handleRotateRight();
    }
  }, [handleNext, handlePrevious, handleRotateLeft, handleRotateRight, handleResetZoom, handleZoomIn, handleZoomOut, isDrawingMode]);

  useEffect(() => {
    if (!isDragging) return;

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp, isDragging]);

  // Add keyboard shortcuts when dialog opens
  useEffect(() => {
    if (!open) return;

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: '#000',
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          backgroundColor: '#000',
        }}
      >
        {/* Top Bar with Close and Actions - Desktop */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
            zIndex: 10,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: '#fff',
                fontSize: '14px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.5,
              }}
            >
              {fileName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {uploadedDate && (
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '12px',
                  }}
                >
                  {formatDate(uploadedDate)}
                </Typography>
              )}
              {showCounter && (
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '12px',
                  }}
                >
                  {currentIndex + 1} of {allImages.length}
                </Typography>
              )}
              {renderLinkedRecordLinks()}
            </Box>
          </Box>

          {/* Desktop Controls */}
          <Box sx={{ display: 'flex', gap: 1, ml: 2, alignItems: 'center' }}>
            {/* Drawing Controls */}
            {isDrawingMode && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pr: 1, borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <Tooltip title="Brush Color">
                  <Box
                    component="input"
                    type="color"
                    value={drawingColor}
                    onChange={(e) => setDrawingColor(e.target.value)}
                    sx={{
                      width: 32,
                      height: 32,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
                <Tooltip title={`Brush Size: ${brushSize}px`}>
                  <Box
                    component="input"
                    type="range"
                    min="1"
                    max="20"
                    value={brushSize}
                    onChange={(e) => setBrushSize(parseInt(e.target.value))}
                    sx={{
                      width: 80,
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
                <IconButton
                  onClick={handleClearDrawing}
                  sx={{
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title="Clear Drawing"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
                <IconButton
                  onClick={handleSaveDrawing}
                  disabled={isSavingDrawing}
                  sx={{
                    color: '#4caf50',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    },
                  }}
                  title="Save Drawing"
                  size="small"
                >
                  {isSavingDrawing ? <CircularProgress size={20} /> : <CheckIcon />}
                </IconButton>
              </Box>
            )}

            {/* Zoom Controls */}
            {!isDrawingMode && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pr: 1, borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoom <= MIN_ZOOM}
                  sx={{
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title="Zoom Out (–)"
                  size="small"
                >
                  <RemoveIcon />
                </IconButton>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '12px',
                    minWidth: '45px',
                    textAlign: 'center',
                  }}
                >
                  {Math.round(zoom * 100)}%
                </Typography>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoom >= MAX_ZOOM}
                  sx={{
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title="Zoom In (+)"
                  size="small"
                >
                  <AddIcon />
                </IconButton>
                <IconButton
                  onClick={handleResetZoom}
                  disabled={zoom === 1 && panX === 0 && panY === 0}
                  sx={{
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title="Reset Zoom (0)"
                  size="small"
                >
                  <FitScreenIcon />
                </IconButton>
                <IconButton
                  onClick={handleRotateLeft}
                  sx={{
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title="Rotate Left ([)"
                  size="small"
                >
                  <RotateLeftIcon />
                </IconButton>
                <IconButton
                  onClick={handleRotateRight}
                  sx={{
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title="Rotate Right (])"
                  size="small"
                >
                  <RotateRightIcon />
                </IconButton>
              </Box>
            )}

            {/* Pin Mode Toggle */}
            {!readOnly && isPlanImage && onAddPin && (
              <Tooltip title={enablePinMode ? "Click image to place pin" : "Enable pin placement mode"}>
                <IconButton
                  onClick={onTogglePinMode}
                  sx={{
                    color: enablePinMode ? '#4caf50' : '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title={enablePinMode ? "Pin mode active" : "Enable pin mode"}
                >
                  <PushPinIcon />
                </IconButton>
              </Tooltip>
            )}

            {!readOnly && (savedPlanFileRef.current || currentImage)?.blob_path && (
              <Tooltip title="Version history">
                <IconButton onClick={openVersionHistory} sx={{ color: '#fff' }}>
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Drawing Mode Toggle */}
            {!readOnly && (
              <Tooltip title={isDrawingMode ? "Disable drawing mode" : "Enable drawing mode"}>
                <IconButton
                  onClick={handleToggleDrawingMode}
                  sx={{
                    color: isDrawingMode ? '#4caf50' : '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title={isDrawingMode ? "Drawing mode active" : "Enable drawing mode"}
                >
                  <DrawIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Share actions (plans only) */}
            {!readOnly && isPlanImage && canShare && (
              <>
                <Tooltip title="Copy share link">
                  <IconButton
                    onClick={handleShareLink}
                    sx={{
                      color: '#fff',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                    }}
                    title="Share link"
                    size="small"
                  >
                    <ShareIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export plan PDF">
                  <IconButton
                    onClick={handleExportPlanPdf}
                    sx={{
                      color: '#fff',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                    }}
                    title="Export plan PDF"
                    size="small"
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* Action Buttons */}
            {/* Download button removed per request */}
            {!readOnly && onDelete && (
              <IconButton
                onClick={() => onDelete(allImages[currentIndex])}
                disabled={!allImages[currentIndex]}
                sx={{
                  color: '#ef5350',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 83, 80, 0.1)',
                  },
                }}
                title="Delete"
              >
                <DeleteIcon />
              </IconButton>
            )}
            <IconButton
              onClick={onClose}
              sx={{
                color: '#fff',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Mobile Top Bar - Title only */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
            zIndex: 10,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: '#fff',
                fontSize: '14px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.5,
              }}
            >
              {fileName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {uploadedDate && (
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '12px',
                  }}
                >
                  {formatDate(uploadedDate)}
                </Typography>
              )}
              {showCounter && (
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '12px',
                  }}
                >
                  {currentIndex + 1} of {allImages.length}
                </Typography>
              )}
              {renderLinkedRecordLinks()}
            </Box>
          </Box>
        </Box>

        {/* Main Content Area */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          {/* Image Container with Navigation */}
          <Box
            ref={imageContainerRef}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onDragStart={handleDragStart}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: '#000',
              position: 'relative',
              cursor: enablePinMode && isPlanImage ? 'crosshair' : (canPan ? (isDragging ? 'grabbing' : 'grab') : 'default'),
              userSelect: 'none',
              touchAction: zoom > 1 ? 'none' : 'auto',
            }}
          >
            {/* Previous Button */}
            {canNavigate && (
              <IconButton
                onClick={handlePrevious}
                disabled={isLoading}
                sx={{
                  position: 'absolute',
                  left: 16,
                  zIndex: 5,
                  color: '#fff',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
                title="Previous (←)"
              >
                <KeyboardArrowDownIcon sx={{ transform: 'rotate(90deg)' }} />
              </IconButton>
            )}

            {/* Wrapper for Image and Pins to ensure synchronized movement */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
              style={{
                transform: zoom > 1 
                  ? `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)` 
                  : 'scale(1) translate(0, 0)',
                transformOrigin: 'center',
                transition: isDragging || isWheelZooming ? 'none' : 'transform 0.1s ease-out',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transformOrigin: 'center',
                  transition: 'transform 0.15s ease-out',
                }}
              >
                {/* Image */}
                {imageUrl && (
                  <img
                    ref={imageRef}
                    src={getDisplayUrl(savedPlanUrl || imageUrl)}
                    alt={fileName}
                    draggable="false"
                    onClick={handleImageClick}
                    onLoad={() => {
                      updateImageLayout();
                      setImageLoaded(true);
                    }}
                    onError={() => setImageLoaded(true)}
                    style={{
                      cursor: 'inherit',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      userSelect: 'none',
                      display: 'block',
                      pointerEvents: enablePinMode && isPlanImage ? 'auto' : 'none',
                    }}
                  />
                )}

                {/* Drawing Canvas Overlay */}
                {isDrawingMode && imageRef.current && (
                  <canvas
                    ref={drawingCanvasRef}
                    onMouseDown={handleDrawingMouseDown}
                    onMouseMove={handleDrawingMouseMove}
                    onMouseUp={handleDrawingMouseUp}
                    onMouseLeave={handleDrawingMouseLeave}
                    onTouchStart={handleDrawingTouchStart}
                    onTouchMove={handleDrawingTouchMove}
                    onTouchEnd={handleDrawingTouchEnd}
                    style={{
                      position: 'absolute',
                      top: imageLayout.top,
                      left: imageLayout.left,
                      width: imageLayout.width,
                      height: imageLayout.height,
                      cursor: 'crosshair',
                      zIndex: 5,
                      transformOrigin: 'top left',
                      touchAction: 'none',
                    }}
                  />
                )}

                {/* Pin Markers */}
                {isPlanImage && pins && pins.length > 0 && !isDrawingMode && (
                  <div style={{
                      position: 'absolute',
                      top: imageLayout.top,
                      left: imageLayout.left,
                      width: imageLayout.width,
                      height: imageLayout.height,
                      pointerEvents: 'none'
                  }}>
                  {pins.map((pin) => {
                  // Calculate pin position as percentage of image
                  // Handle both PascalCase (PinX/PinY) and snake_case (pin_x/pin_y) property names
                  const pinPercentLeft = pin.PinX !== undefined ? pin.PinX : pin.pin_x;
                  const pinPercentTop = pin.PinY !== undefined ? pin.PinY : pin.pin_y;
                  
                  // Skip pins without valid coordinates
                  if (pinPercentLeft === undefined || pinPercentTop === undefined || 
                      pinPercentLeft === null || pinPercentTop === null) {
                    return null;
                  }

                  // Check if this pin is selected
                  const isSelected = selectedPin && selectedPin.activity_id === pin.activity_id;
                  
                  return (
                    <Tooltip 
                      key={pin.activity_id} 
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{pin.title}</Typography>
                          {pin.description && (
                            <Typography variant="caption">{pin.description}</Typography>
                          )}
                        </Box>
                      }
                      placement="top"
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onPinClick) onPinClick(pin);
                        }}
                        sx={{
                          position: 'absolute',
                          left: `${pinPercentLeft}%`,
                          top: `${pinPercentTop}%`,
                          transform: `translate(-50%, -100%) rotate(${-rotation}deg)`,
                          zIndex: isSelected ? 20 : 10,
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          '&:hover': {
                            filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.8))',
                          },
                        }}
                      >
                        <LocationOnIcon
                          sx={{
                            fontSize: (isSelected ? 42 : 32) / zoom,
                            color: isSelected 
                              ? '#f26100ff'
                              : (pin.is_completed ? '#4caf50' : '#c00000ff'),
                            filter: isSelected 
                              ? 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.2))'
                              : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.22))',
                            transition: 'all 0.2s ease',
                          }}
                        />
                      </Box>
                    </Tooltip>
                  );
                  })}
                  </div>
                )}
              </Box>
            </Box>

            {/* Loading Indicator Overlay */}
            {(isLoading || (open && imageUrl && !imageLoaded)) && (
              <Box
                role="status"
                aria-label="Loading image"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 30,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                }}
              >
                <CircularProgress size={60} sx={{ color: '#fff' }} />
              </Box>
            )}

            {/* Next Button */}
            {canNavigate && (
              <IconButton
                onClick={handleNext}
                disabled={isLoading}
                sx={{
                  position: 'absolute',
                  right: 16,
                  zIndex: 5,
                  color: '#fff',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
                title="Next (→)"
              >
                <KeyboardArrowDownIcon sx={{ transform: 'rotate(-90deg)' }} />
              </IconButton>
            )}

            {/* Zoom hint when hovering */}
            {showZoomHint && zoom > 1 && !enablePinMode && !isDrawingMode && (
              <Box
                onClick={handleResetZoom}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 500,
                  pointerEvents: 'auto',
                  zIndex: 100,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  animation: 'slideUp 0.3s ease-out',
                  '@keyframes slideUp': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateX(-50%) translateY(10px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateX(-50%) translateY(0)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.6)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Reset zoom
              </Box>
            )}

            {/* Pin mode hint */}
            {enablePinMode && isPlanImage && !isDrawingMode && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(76, 175, 80, 0.9)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <PushPinIcon sx={{ fontSize: 16 }} />
                Click on the plan to place a pin
              </Box>
            )}

            {/* Drawing mode hint */}
            {isDrawingMode && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(100, 150, 255, 0.9)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <DrawIcon sx={{ fontSize: 16 }} />
                Draw on the image - click Save to apply changes
              </Box>
            )}
          </Box>

          {/* Side Pane for Activity Details */}
          {selectedPin && (
            <Box
              sx={{
                width: { xs: '100%', md: 400 },
                height: '100%',
                backgroundColor: '#000',
                borderLeft: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                flexDirection: 'column',
                position: { xs: 'absolute', md: 'relative' },
                zIndex: 20,
                top: 0,
                right: 0,
                boxShadow: { xs: '-4px 0 20px rgba(0,0,0,0.5)', md: 'none' },
              }}
            >
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <IconButton
                  onClick={onClosePinDetails}
                  sx={{
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title="Close activity details"
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <ActivityDetails 
                  key={`${selectedPin.activity_id}-${selectedPin.title}-${selectedPin.is_completed}-${refreshKey}`}
                  activityId={selectedPin.activity_id} 
                  workers={workers} 
                  onEdit={onEditActivity}
                  onDelete={onDeleteActivity}
                  initialActivityData={selectedPin}
                  readOnly={readOnly}
                  resolvePublicPhotoUrl={resolvePublicPhotoUrl}
                  onOpenPlan={onOpenPlan}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Mobile Bottom Control Bar */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            justifyContent: 'space-around',
            alignItems: 'center',
            padding: '12px 8px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            zIndex: 10,
            gap: 0.5,
            flexWrap: 'wrap',
          }}
        >
          {/* Drawing Controls for Mobile */}
          {isDrawingMode && (
            <>
              <Tooltip title="Brush Color">
                <Box
                  component="input"
                  type="color"
                  value={drawingColor}
                  onChange={(e) => setDrawingColor(e.target.value)}
                  sx={{
                    width: 28,
                    height: 28,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
              <Tooltip title={`Size: ${brushSize}`}>
                <Box
                  component="input"
                  type="range"
                  min="1"
                  max="20"
                  value={brushSize}
                  onChange={(e) => setBrushSize(parseInt(e.target.value))}
                  sx={{
                    width: 60,
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
              <IconButton
                onClick={handleClearDrawing}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                title="Clear"
                size="small"
              >
                <ClearIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                onClick={handleSaveDrawing}
                disabled={isSavingDrawing}
                sx={{
                  color: '#4caf50',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  },
                }}
                title="Save"
                size="small"
              >
                {isSavingDrawing ? <CircularProgress size={20} /> : <CheckIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </>
          )}

          {/* Zoom Out */}
          {!isDrawingMode && (
            <>
              <IconButton
                onClick={handleZoomOut}
                disabled={zoom <= MIN_ZOOM}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                title="Zoom Out (–)"
                size="small"
              >
                <RemoveIcon sx={{ fontSize: 20 }} />
              </IconButton>

              {/* Zoom Display */}
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '11px',
                  minWidth: '40px',
                  textAlign: 'center',
                }}
              >
                {Math.round(zoom * 100)}%
              </Typography>

              {/* Zoom In */}
              <IconButton
                onClick={handleZoomIn}
                disabled={zoom >= MAX_ZOOM}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                title="Zoom In (+)"
                size="small"
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>

              {/* Reset Zoom */}
              <IconButton
                onClick={handleResetZoom}
                disabled={zoom === 1 && panX === 0 && panY === 0}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                title="Reset Zoom (0)"
                size="small"
              >
                <FitScreenIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <IconButton
                onClick={handleRotateLeft}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                title="Rotate Left ([)"
                size="small"
              >
                <RotateLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <IconButton
                onClick={handleRotateRight}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                title="Rotate Right (])"
                size="small"
              >
                <RotateRightIcon sx={{ fontSize: 20 }} />
              </IconButton>

              {/* Divider */}
              <Box
                sx={{
                  width: '1px',
                  height: '24px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  mx: 0.5,
                }}
              />
            </>
          )}

          {/* Pin Mode Toggle (only for plan images) */}
          {isPlanImage && onAddPin && (
            <Tooltip title={enablePinMode ? "Click image to place pin" : "Enable pin placement mode"}>
              <IconButton
                onClick={onTogglePinMode}
                sx={{
                  color: enablePinMode ? '#4caf50' : '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                title={enablePinMode ? "Pin mode active" : "Enable pin mode"}
                size="small"
              >
                <PushPinIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {!readOnly && (savedPlanFileRef.current || currentImage)?.blob_path && (
            <Tooltip title="Version history">
              <IconButton onClick={openVersionHistory} sx={{ color: '#fff' }} size="small">
                <HistoryIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Drawing Mode Toggle */}
          <Tooltip title={isDrawingMode ? "Disable drawing mode" : "Enable drawing mode"}>
            <IconButton
              onClick={handleToggleDrawingMode}
              sx={{
                color: isDrawingMode ? '#4caf50' : '#fff',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              title={isDrawingMode ? "Drawing mode active" : "Enable drawing mode"}
              size="small"
            >
              <DrawIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Download */}
          {/* Mobile download button removed per request */}

          {/* Delete */}
          {onDelete && (
            <IconButton
              onClick={() => onDelete(allImages[currentIndex])}
              disabled={!allImages[currentIndex]}
              sx={{
                color: '#ef5350',
                '&:hover': {
                  backgroundColor: 'rgba(239, 83, 80, 0.1)',
                },
              }}
              title="Delete"
              size="small"
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}

          {/* Close */}
          <IconButton
            onClick={onClose}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
            title="Close"
            size="small"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Save Message Snackbar */}
      <Snackbar
        open={showSaveMessage}
        autoHideDuration={6000}
        onClose={() => setShowSaveMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSaveMessage(false)}
          severity={saveMessageType}
          sx={{ width: '100%' }}
        >
          {saveMessage}
        </Alert>
      </Snackbar>

      {/* Image version history */}
      <Dialog open={historyOpen} onClose={() => !historyRestoring && setHistoryOpen(false)}
        onClick={(event) => event.stopPropagation()} maxWidth="sm" fullWidth>
        <DialogTitle>Previous versions</DialogTitle>
        <DialogContent sx={{ maxHeight: '55vh' }} onScroll={(event) => {
          const area = event.currentTarget;
          if (area.scrollHeight - area.scrollTop - area.clientHeight < 120) {
            void loadOlderVersions();
          }
        }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Old versions are retained for 180 days.
          </Typography>
          {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}
          {historyLoading && historyVersions.length === 0 && <CircularProgress size={24} />}
          {!historyLoading && historyVersions.length === 0 && !historyError && (
            <Typography>No earlier versions are available.</Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {historyVersions.map((version) => (
              <Box key={version.version_id} component="button" type="button"
                onClick={() => setSelectedVersion(version)}
                aria-label={`Select version from ${new Date(version.saved_at).toLocaleString()}`}
                sx={{ p: 1.5, borderRadius: 1, cursor: 'pointer', backgroundColor: 'background.paper', textAlign: 'left',
                  border: selectedVersion?.version_id === version.version_id ? '2px solid #1976d2' : '1px solid #aaa' }}>
                <Typography variant="body2" display="block">
                  {new Date(version.saved_at).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
          {historyCursor && <Button onClick={loadOlderVersions} disabled={historyLoading} sx={{ mt: 2 }}>
            {historyLoading ? 'Loading…' : 'Load more versions'}
          </Button>}
          {selectedVersion && <Typography sx={{ mt: 2 }}>
            Restore the image from {new Date(selectedVersion.saved_at).toLocaleString()}? The current image will remain in version history.
          </Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)} disabled={historyRestoring}>Cancel</Button>
          <Button onClick={restoreSelectedVersion} variant="contained" disabled={!selectedVersion || historyRestoring}>
            {historyRestoring ? 'Restoring…' : 'Restore selected version'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Link Dialog Fallback */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Link created. Anyone with this link can view this plan and its pins.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={shareDialogLink}
              InputProps={{
                readOnly: true,
              }}
              onFocus={(e) => e.target.select()}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyFromDialog}
              startIcon={<ContentCopyIcon />}
              sx={{ ml: 1, height: '40px' }}
              disableElevation
            >
              Copy
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ImageViewer;
